import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Title from "@/components/ui/Title/Title.tsx";
import Loader from "@/components/ui/Loader/Loader.tsx";
import Button from "@/components/ui/Buttons/Button/Button.tsx";
import IconReset from "@/assets/icons/reset.svg?react";
import IconChevronLeft from "@/assets/icons/chevron-left.svg?react";
import IconRadioChecked from "@/assets/icons/radio-checked.svg?react";
import { AnsweredQuestion, ResolvedProduct } from "@/features/widgets/components/Comparison/types/ComparisonTypes";
import { WinningProduct } from "@/features/widgets/components/Comparison/WinningProduct.tsx";
import IconWand from "@/assets/icons/wand.svg?react";

import "./styles/Advice.scss";

type AdviceQuestionOption = {
  value: string;
  answer: string;
  answer_explanation: string;
  products: number[];
  weight: number;
}

type AdviceQuestion = {
  label: string;
  question: string;
  options: AdviceQuestionOption[],
}

export type AdviceQuestionsResult = {
  questions: AdviceQuestion[];
}

type AdviceProps = {
  questions?: AdviceQuestion[];
  products: ResolvedProduct[];
  loading: boolean;
  onComplete?: (bestProductId: number | null) => void;
  open: boolean;
  onClose: () => void;
}

const CLASS_PREFIX = `ws-widgets-comparison-advice`;

// Must match the opacity transition duration in Advice.scss.
const FADE_DURATION_MS = 200;

export const ComparisonAdvice = ({ questions, products, loading, onComplete, open, onClose }: AdviceProps) => {
  const { t } = useTranslation();

  const panelClasses = [
    `${ CLASS_PREFIX }-panel`,
    open ? `${ CLASS_PREFIX }-panel--opened` : ``,
  ];

  const backdropClasses = [
    `${ CLASS_PREFIX }-backdrop`,
    open ? `${ CLASS_PREFIX }-backdrop--visible` : ``,
  ];

  return (
    <>
      <div
        className={ backdropClasses.join(` `) }
        onClick={ onClose }
        aria-hidden="true"
      />
      <aside className={ panelClasses.join(` `) } aria-hidden={ !open }>
        <div className={ `${ CLASS_PREFIX }-panel__inner` }>
          <div className={ `${ CLASS_PREFIX }-panel__header` }>
            <Title level={ 3 } className={ `${ CLASS_PREFIX }-panel__title` }>
              <IconWand/> { t(`comparison.advice.title`) }
            </Title>
            <Button onClick={ onClose } className={ `${ CLASS_PREFIX }-panel__close` } variant={ `tertiary` }>
              { t(`core.close`) }
            </Button>
          </div>
          <div className={ `${ CLASS_PREFIX }-panel__content` }>
            { loading &&
              <Loader
                text={ t('comparison.advice.loading') }
                extraTexts={ [
                  t('comparison.advice.loading_extra'),
                  t('comparison.advice.loading_extra_2'),
                ] }
                horizontalAlign={ `left` }
              />
            }
            { !loading && questions && questions.length > 0 &&
              <Quiz questions={ questions } products={ products } onComplete={ onComplete }/>
            }
            { !loading && (!questions || questions.length === 0) &&
              <p>{ t('comparison.advice.unavailable') }</p>
            }
          </div>
        </div>
      </aside>
    </>
  )
}

type ProductScores = Record<number, number>;

type HistoryEntry = {
  remainingQuestions: AdviceQuestion[];
  candidateIds: number[];
  answeredQuestions: AnsweredQuestion[];
  scores: ProductScores;
  totalPoints: number;
  answeredCount: number;
}

// Whether any remaining candidate still has an option under this question.
const stillHasCandidate = (question: AdviceQuestion, candidateIds: Set<number>): boolean =>
  question.options.some(option => option.products.some(productId => candidateIds.has(productId)));

// Distinct products a question covers, for ordering the least differentiating first.
const countQuestionProducts = (question: AdviceQuestion): number =>
  new Set(question.options.flatMap(option => option.products)).size;

const sortByFewestProducts = (questions: AdviceQuestion[]): AdviceQuestion[] =>
  [...questions].sort((a, b) => countQuestionProducts(a) - countQuestionProducts(b));

type QuizProps = {
  questions: AdviceQuestion[];
  products: ResolvedProduct[];
  onComplete?: (bestProductId: number | null) => void;
}

const Quiz = ({ questions, products, onComplete }: QuizProps) => {
  const { t } = useTranslation();
  const [remainingQuestions, setRemainingQuestions] = useState<AdviceQuestion[]>(() => sortByFewestProducts(questions));
  const [candidateIds, setCandidateIds] = useState<Set<number>>(() => new Set(products.map(product => product.id)));
  const [scores, setScores] = useState<ProductScores>({});
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [answeredCount, setAnsweredCount] = useState<number>(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<AnsweredQuestion[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const fadeTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(fadeTimeout.current);
    setRemainingQuestions(sortByFewestProducts(questions));
    setCandidateIds(new Set(products.map(product => product.id)));
    setScores({});
    setTotalPoints(0);
    setAnsweredCount(0);
    setAnsweredQuestions([]);
    setHistory([]);
    setIsVisible(true);
    setSelectedIndex(null);
    // `products` deliberately omitted: a fresh reference each render would reset the quiz.
  }, [questions]); // eslint-disable-line react-hooks/exhaustive-deps

  const isComplete = remainingQuestions.length === 0;

  useEffect(() => {
    onComplete?.(isComplete ? getBestProduct(scores)?.id ?? null : null);
  }, [isComplete, scores, onComplete]);

  useEffect(() => () => clearTimeout(fadeTimeout.current), []);

  const selectOption = (option: AdviceQuestionOption, index: number) => {
    setHistory(previousHistory => [
      ...previousHistory,
      { remainingQuestions, candidateIds: Array.from(candidateIds), answeredQuestions, scores, totalPoints, answeredCount },
    ]);

    setScores(previousScores => {
      const nextScores = { ...previousScores };
      option.products.forEach(productId => {
        nextScores[productId] = (nextScores[productId] ?? 0) + option.weight;
      });
      return nextScores;
    });

    // No products means a neutral answer - no points, and it's left out of the spec list.
    if (option.products.length > 0) {
      setTotalPoints(previousTotal => previousTotal + option.weight);
      setAnsweredCount(previousCount => previousCount + 1);
      setAnsweredQuestions(previousAnswered => [
        ...previousAnswered,
        {
          label: question.label,
          value: option.value,
          productIds: option.products,
          options: question.options.map(questionOption => ({
            value: questionOption.value,
            productIds: questionOption.products,
          })),
        },
      ]);
    }

    // Narrow to this answer's products, unless that would leave zero candidates (safety valve).
    let nextCandidateIds = candidateIds;
    if (option.products.length > 0) {
      const attemptedCandidateIds = new Set(
        Array.from(candidateIds).filter(productId => option.products.includes(productId))
      );

      if (attemptedCandidateIds.size > 0) {
        nextCandidateIds = attemptedCandidateIds;
      }
    }
    setCandidateIds(nextCandidateIds);

    // Also drop any queued question that no longer mentions a remaining candidate.
    const nextQuestions = remainingQuestions
      .slice(1)
      .filter(remainingQuestion => stillHasCandidate(remainingQuestion, nextCandidateIds));

    setSelectedIndex(index);
    setIsVisible(false);
    fadeTimeout.current = setTimeout(() => {
      setRemainingQuestions(nextQuestions);
      setIsVisible(true);
      setSelectedIndex(null);
    }, FADE_DURATION_MS);
  };

  const goBack = () => {
    if (history.length === 0) {
      return;
    }

    const previousEntry = history[history.length - 1];

    clearTimeout(fadeTimeout.current);
    setScores(previousEntry.scores);
    setTotalPoints(previousEntry.totalPoints);
    setAnsweredCount(previousEntry.answeredCount);
    setAnsweredQuestions(previousEntry.answeredQuestions);
    setCandidateIds(new Set(previousEntry.candidateIds));
    setRemainingQuestions(previousEntry.remainingQuestions);
    setHistory(previousHistory => previousHistory.slice(0, -1));
    setIsVisible(true);
    setSelectedIndex(null);
  };

  const restart = () => {
    clearTimeout(fadeTimeout.current);
    setRemainingQuestions(sortByFewestProducts(questions));
    setCandidateIds(new Set(products.map(product => product.id)));
    setScores({});
    setTotalPoints(0);
    setAnsweredCount(0);
    setAnsweredQuestions([]);
    setHistory([]);
    setIsVisible(true);
    setSelectedIndex(null);
  };

  const fadeClasses = [
    `${ CLASS_PREFIX }__fade`,
    isVisible ? `` : `${ CLASS_PREFIX }__fade--hidden`,
  ].join(` `);

  if (isComplete) {
    return (
      <div className={ `${ CLASS_PREFIX }` }>
        <div className={ fadeClasses }>
          <QuizResult
            scores={ scores }
            answeredQuestions={ answeredQuestions }
            products={ products }
            onRestart={ restart }
          />
        </div>
      </div>
    );
  }

  const question = remainingQuestions[0];

  const neutralOption: AdviceQuestionOption = {
    value: ``,
    answer: t(`comparison.advice.no_preference`),
    answer_explanation: ``,
    products: [],
    weight: 0,
  };

  const optionsWithNeutral = [...question.options, neutralOption];

  return (
    <div className={ `${ CLASS_PREFIX }` }>
      <div className={ fadeClasses }>
        <div className={ `${ CLASS_PREFIX }__question` }>
          <Title level={ 4 }>{ question.question }</Title>
          <div className={ `${ CLASS_PREFIX }__options` }>
            { optionsWithNeutral.map((option, index) => {
              const optionClasses = [
                `${ CLASS_PREFIX }__option`,
                selectedIndex === index ? `${ CLASS_PREFIX }__option--selected` : ``,
              ].join(` `);

              return (
                <button onClick={ () => selectOption(option, index) } className={ optionClasses } key={ index }>
                  <span className={ `${ CLASS_PREFIX }__option-icon` }>
                    <IconRadioChecked/>
                  </span>
                  <span className={ `${ CLASS_PREFIX }__option-text` }>
                    <span className={ `${ CLASS_PREFIX }__option-label` }>
                      { option.answer }
                    </span>
                    { option.answer_explanation ?
                      <span className={ `${ CLASS_PREFIX }__option-explanation` }>
                        { option.answer_explanation }
                      </span>
                      :
                      null
                    }
                  </span>
                </button>
              );
            }) }
          </div>

          { history.length > 0 ?
            <Button onClick={ goBack } className={ `${ CLASS_PREFIX }__back` }>
              <span className={ `${ CLASS_PREFIX }__back-icon` }>
                <IconChevronLeft/>
              </span>
              { t(`comparison.advice.back`) }
            </Button>
            :
            null
          }
        </div>
      </div>
    </div>
  )
}

type BestProduct = {
  id: number;
  score: number;
}

const getBestProduct = (scores: ProductScores): BestProduct | null => {
  let best: BestProduct | null = null;

  Object.entries(scores).forEach(([productId, score]) => {
    if (!best || score > best.score) {
      best = { id: Number(productId), score };
    }
  });

  return best;
}

type QuizResultProps = {
  scores: ProductScores;
  answeredQuestions: AnsweredQuestion[];
  products: ResolvedProduct[];
  onRestart: () => void;
}

const QuizResult = ({ scores, answeredQuestions, products, onRestart }: QuizResultProps) => {
  const { t } = useTranslation();

  const bestProduct = getBestProduct(scores);
  const matchedProduct = products.find(product => product.id === bestProduct?.id);

  const matchedProductId = matchedProduct?.id;
  const matchedCount = matchedProductId === undefined
    ? 0
    : answeredQuestions.filter(answeredQuestion => answeredQuestion.productIds.includes(matchedProductId)).length;
  const percentage = matchedProduct && answeredQuestions.length > 0
    ? Math.round((matchedCount / answeredQuestions.length) * 100)
    : null;

  return (
    <div className={ `${ CLASS_PREFIX }__result` }>
      {
        matchedProduct
          ?
          <>
            <Title level={ 4 }>{ t(`comparison.advice.result_title`) }</Title>
            <p className={ `${ CLASS_PREFIX }__result` }>
              { t('comparison.advice.result_intro') }
            </p>
            <WinningProduct
              product={ matchedProduct }
              products={ products }
              percentage={ percentage }
              answeredQuestions={ answeredQuestions }
            />
          </>
          :
          <p>{ t(`comparison.advice.no_result`) }</p>
      }
      <Button className={ `${ CLASS_PREFIX }__restart` } onClick={ onRestart } variant={ `secondary` }>
        <span className={ `${ CLASS_PREFIX }__restart-icon` }>
          <IconReset/>
        </span>
        { t(`comparison.advice.restart`) }
      </Button>
    </div>
  )
}
